// ============================================================
// WorkspaceNotificationItem — one compact signal card.
//
// Channel-accented icon tile with glow, two-line clamped title,
// time metadata, new-signal dot, urgent chip. Hover lifts the
// card into its channel accent (NotificationBar reference).
// ============================================================

import { useState } from 'react'
import {
  tint,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
} from './workspaceTheme'

export default function WorkspaceNotificationItem({
  accent = WORKSPACE_ACCENTS.cyan,
  glyph,               // string or ReactNode
  title,
  body,
  time,
  channelLabel,
  isNew = false,
  urgent = false,
  onOpen,
  compact = false,
  style: styleOverride,
}) {
  const [hover, setHover] = useState(false)
  const clickable = typeof onOpen === 'function'

  return (
    <div
      onClick={clickable ? onOpen : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } } : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 10 : 12,
        padding: compact ? '9px 10px' : '11px 12px',
        borderRadius: 10,
        background: hover && clickable ? tint(accent, 9) : WORKSPACE_SURFACE.card,
        border: `1px solid ${hover && clickable ? tint(accent, 24) : WORKSPACE_SURFACE.lineFaint}`,
        transition: 'all 0.2s ease',
        cursor: clickable ? 'pointer' : 'default',
        minWidth: 0,
        ...styleOverride,
      }}
    >
      {glyph != null && (
        <div
          aria-hidden
          style={{
            width: compact ? 28 : 32,
            height: compact ? 28 : 32,
            borderRadius: 9,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: compact ? 12 : 14,
            color: accent,
            background: tint(accent, 13),
            border: `1px solid ${tint(accent, 22)}`,
            boxShadow: `0 0 10px ${tint(accent, 12)}`,
          }}
        >
          {glyph}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: compact ? 11.5 : 12.5,
            fontWeight: 600,
            color: WORKSPACE_TEXT.primary,
            lineHeight: 1.45,
            fontFamily: WORKSPACE_FONT.ui,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </div>
        {body && (
          <div
            style={{
              marginTop: 2,
              fontSize: 11,
              color: WORKSPACE_TEXT.tertiary,
              lineHeight: 1.5,
              fontFamily: WORKSPACE_FONT.ui,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {body}
          </div>
        )}
        <div
          style={{
            marginTop: 3,
            fontSize: 9.5,
            color: WORKSPACE_TEXT.muted,
            fontFamily: WORKSPACE_FONT.mono,
            letterSpacing: '0.04em',
          }}
        >
          {[channelLabel, time].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 5,
          flexShrink: 0,
        }}
      >
        {isNew && (
          <span
            aria-label="new"
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: accent,
              boxShadow: `0 0 10px ${tint(accent, 55)}`,
            }}
          />
        )}
        {urgent && (
          <span
            style={{
              fontSize: 8,
              padding: '2px 6px',
              borderRadius: 5,
              background: tint(WORKSPACE_ACCENTS.red, 16),
              border: `1px solid ${tint(WORKSPACE_ACCENTS.red, 30)}`,
              color: WORKSPACE_ACCENTS.red,
              fontWeight: 700,
              letterSpacing: '0.1em',
              fontFamily: WORKSPACE_FONT.mono,
            }}
          >
            URGENT
          </span>
        )}
      </div>
    </div>
  )
}
